import { Button, Table, TableProps, Tag } from 'antd'
import { CheckCircleOutlined, CloseSquareOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/lib/table'
import { CButton } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import {
  path,
  useColumnLocalFilter,
  useColumnLocalSearch,
  useColumnLocalSort,
  useTableSearchParams,
} from 'src/libs'
import { Account } from '../../types'
import { getRecoveryTime } from './getRecoveryTime'

type AccountsListProps = {
  items: Account[] | undefined
  loading: boolean
  type: 'activeReal' | 'activeBonus' | 'activeDemo' | 'activeDemoContest' | 'archive'
}

const getFixRateValue = (account: Account): number =>
  account.fixRateValue || (account.isFixRate ? 10000 : 0)

export const AccountsTable = ({ items, loading, type }: AccountsListProps) => {
  const searchFields = [
    'accountId',
    'mtLogin',
    'accountName',
    'tariff',
    'currency',
    'balance',
    'leverage',
    'tradedLots',
  ]
  const sorterFields = ['accountId']
  const filterFields = ['fixRateValue', 'isSwapFree']

  const handleTableChange: TableProps<Account>['onChange'] =
    useTableSearchParams({
      searchFields,
      filterFields,
      sorterFields,
    })

  const accountIdSearch = useColumnLocalSearch<Account>({
    dataIndex: 'accountId',
  })
  const mtLoginSearch = useColumnLocalSearch<Account>({
    dataIndex: 'mtLogin',
  })
  const accountNameSearch = useColumnLocalSearch<Account>({
    dataIndex: 'accountName',
  })
  const tariffSearch = useColumnLocalSearch<Account>({
    dataIndex: 'tariff',
  })
  const currencySearch = useColumnLocalSearch<Account>({
    dataIndex: 'currency',
  })
  const balanceSearch = useColumnLocalSearch<Account>({
    dataIndex: 'balance',
  })
  const leverageSearch = useColumnLocalSearch<Account>({
    dataIndex: 'leverage',
  })
  const tradedLotsSearch = useColumnLocalSearch<Account>({
    dataIndex: 'tradedLots',
  })

  const columns: ColumnsType<Account> = [
    {
      title: 'ID',
      dataIndex: 'accountId',
      ...accountIdSearch,
      ...useColumnLocalSort<Account>({
        dataIndex: 'accountId',
        initialValue: 'descend',
      }),
      sorter: (a, b) => parseInt(a.accountId) - parseInt(b.accountId),
    },
    {
      title: 'MT Login',
      dataIndex: 'mtLogin',
      ...mtLoginSearch,
    },
    {
      title: 'Open RDC',
      render: (record) => (
        <Button
          target="_blank"
          href={`https://rdc.hwmt.link/module/query/info-by-client?login=${record.mtLogin}`}
          style={{
            backgroundColor: 'var(--crm-accent-amber)',
            borderColor: 'var(--crm-accent-amber)',
            color: '#000',
            textDecoration: 'none',
          }}
        >
          Open RDC
        </Button>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'accountName',
      ...accountNameSearch,
    },
    { title: 'Tariff', dataIndex: 'tariff', ...tariffSearch },
    {
      title: 'Currency',
      dataIndex: 'currency',
      ...currencySearch,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      ...balanceSearch,
    },
    {
      title: 'Fix rate',
      dataIndex: 'fixRateValue',
      ...useColumnLocalFilter<Account>({ dataIndex: 'fixRateValue' }),
      onFilter: (value, record) => getFixRateValue(record) === Number(value),
      filters: [
        { text: 'No fix rate', value: 0 },
        { text: '1:10000', value: 10000 },
        { text: '1:15000', value: 15000 },
      ],
      render: (_: number, record) => {
        const fixRateValue = getFixRateValue(record)

        if (fixRateValue === 10000) {
          return <Tag color="blue">10к</Tag>
        }

        if (fixRateValue === 15000) {
          return <Tag color="purple">15к</Tag>
        }

        return null
      },
    },
    {
      title: 'Swap free',
      dataIndex: 'isSwapFree',
      ...useColumnLocalFilter<Account>({ dataIndex: 'isSwapFree' }),
      onFilter: (value, record) => record.isSwapFree === value,
      filters: [
        { text: 'Is swap free', value: true },
        { text: 'Not swap free', value: false },
      ],
      render: (isSwapFree: boolean) => (
        <>
          {isSwapFree ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Leverage',
      dataIndex: 'leverage',
      ...leverageSearch,
    },
    {
      title: 'Traded Lots',
      dataIndex: 'tradedLots',
      ...tradedLotsSearch,
    },
    { title: 'Withdrawal available', dataIndex: 'availableForWithdrawal' },
    { title: 'MT Comment', dataIndex: 'mtComment' },
    {
      title: 'Copies',
      render: (item: Account) => {
        return (
          <>
            {item.subscriptions.length === 0 ? (
              <div>no</div>
            ) : (
              <div className="d-flex flex-column">
                {item.subscriptions.map((item) => (
                  <a
                    href={path.copytrade.strategies.details(
                      String(item.strategy.id),
                    )}
                    target={'_blank'}
                  >
                    {item.strategy.title}
                  </a>
                ))}
              </div>
            )}
          </>
        )
      },
    },
    {
      title: 'Public on CT',
      render: (item: Account) => {
        return (
          <>
            {item.strategyArray.length === 0 ? (
              <div>no</div>
            ) : (
              <div className="d-flex flex-column">
                {item.strategyArray.map((item) => (
                  <a
                    href={path.copytrade.strategies.details(String(item.id))}
                    target={'_blank'}
                  >
                    {item.title}
                  </a>
                ))}
              </div>
            )}
          </>
        )
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item: Account) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.clients.accountDetail(item.clientId, item.accountId)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
  ]

  const getColumns = () => {
    const newColumns = [...columns]

    if (type === 'activeReal') {
      newColumns.splice(
        10,
        0,
        ...[
          {
            title: 'Bonus',
            render: (item: Account) => {
              return <>{item.depositBonusAccount?.bonusAmount}</>
            },
          },
          {
            title: 'Bonus for Withdrawal',
            render: (item: Account) => {
              return <>{item.depositBonusAccount?.profitAmount}</>
            },
          },
        ],
      )

      return newColumns
    }

    if (type === 'archive') {
      newColumns.splice(newColumns.length - 1, 0, {
        title: 'Type of archive',
        dataIndex: 'archivingType',
        render: (archivingType: string) => <>{archivingType || '—'}</>,
      }, {
        title: 'Recovery time',
        render: (item: Account) => <>{getRecoveryTime(item.archivedAt)}</>,
      })

      return newColumns
    }
    return newColumns
  }

  return (
    <div>
      <Table
        dataSource={items}
        columns={getColumns()}
        onChange={handleTableChange}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="accountId"
      />
    </div>
  )
}
